import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import LoadingCircle from 'components/ui/LoadingCircle';
import { getDriverPerformanceMetrics } from 'utils/MetricAPIUtils';
import { convertMetersToMiles } from 'utils/distance';
import { titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DISTANCE_UNIT } from 'utils/localization/types';
import { Avatar } from '../modules/user/Avatar';

const TableColumnHeader = (props) => {
  const { label } = props;
  const tableHeaderClassNames = classnames({
    'px-6': 1,
    'py-3': 1,
    'text-left': 1,
    'text-xs': 1,
    'font-medium': 1,
    'text-white': 1,
    uppercase: 1,
    'tracking-wider': 1,
  });
  return (
    <th scope="col" className={tableHeaderClassNames}>
      {label}
    </th>
  );
};

const MetricPercentageCell = (props) => {
  const { value } = props;
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      {(value || value >= 0) && (
        <span className="text-grey-darkest">{value}%</span>
      )}
      {Number.isNaN(value) && (
        <div className="flex items-center">
          <LoadingCircle text="" color="green" />
        </div>
      )}
    </td>
  );
};

const MetricCell = (props) => {
  const { value } = props;
  return (
    <td className="px-6 py-4 whitespace-nowrap">
      {(value || value >= 0) && (
        <span className="text-grey-darkest">{value}</span>
      )}
      {Number.isNaN(value) && (
        <div className="flex items-center">
          <LoadingCircle text="" color="green" />
        </div>
      )}
    </td>
  );
};

const DriverRow = (props) => {
  const {
    driver: { _id, first_name, last_name, username },
    dateFilters,
  } = props;
  const [metrics, setMetrics] = useState({});

  const getMetric = (metricName, results) => {
    const metric = results.find((result) => result.metric === metricName);
    if (metric) {
      if (metricName === 'driverMileageVolume') {
        return convertMetersToMiles(metric.score);
      }
      return metric.score;
    }
    return 0;
  };

  useEffect(() => {
    const getMetrics = async () => {
      const { results } = await getDriverPerformanceMetrics(_id, dateFilters);
      const scores = {
        onTimePercentage: getMetric('onTime', results),
        podQualityPercentage: getMetric('podQuality', results),
        appUsagePercentage: getMetric('driverAppUsage', results),
        totalDistance: getMetric('driverMileageVolume', results),
        totalLoadCount: getMetric('driverLoad', results),
      };
      setMetrics(scores);
    };
    getMetrics();
  }, [props.driver, dateFilters]);

  const formatDistance = (miles) => {
    const distance = miles
      ? new Intl.NumberFormat('en-US').format(Math.round(metrics.totalDistance))
      : 0;
    return `${distance} ${DEFAULT_DISTANCE_UNIT}`;
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            <Avatar
              key={_id}
              userId={_id}
              altName=""
              className="h-10 w-10 rounded-full"
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">
              {titleize(`${first_name} ${last_name}`)}
            </div>
            <div className="text-sm text-gray-500">{`${username || '-'}`}</div>
          </div>
        </div>
      </td>
      <MetricPercentageCell value={Math.round(metrics.onTimePercentage)} />
      <MetricPercentageCell value={Math.round(metrics.podQualityPercentage)} />
      <MetricPercentageCell value={Math.round(metrics.appUsagePercentage)} />
      <MetricCell value={metrics.totalLoadCount} />
      <MetricCell value={formatDistance(metrics.totalDistance)} />
    </tr>
  );
};

const DriverMetricList = (props) => {
  const { drivers, dateFilters } = props;
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-700">
                <tr>
                  <TableColumnHeader label={t('NAME')} />
                  <TableColumnHeader label={t('ON_TIME')} />
                  <TableColumnHeader label={t('POD_QUALITY')} />
                  <TableColumnHeader label={t('APP_USAGE')} />
                  <TableColumnHeader label={t('LOADS')} />
                  <TableColumnHeader label={t('DISTANCE')} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(!drivers || drivers.length === 0) && (
                  <tr>
                    <td colSpan={6} className="p-6">
                      <span className="text-sm text-gray-900">
                        {t('NO_DRIVER_DATA')}...
                      </span>
                    </td>
                  </tr>
                )}
                {drivers &&
                  drivers.length > 0 &&
                  drivers.map((d) => {
                    return (
                      <DriverRow
                        key={d._id}
                        driver={d}
                        dateFilters={dateFilters}
                      />
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverMetricList;
